<template>
  <div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div class="bg-gray-100 p-6 rounded-lg shadow">
        <h2 class="text-2xl font-semibold mb-4">Input Parameters</h2>
        <div class="mb-4 text-sm text-gray-600 italic">
          <p>
            Based on the prompt: 6m steel bars to be cut into pieces of various
            lengths
          </p>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Stock Length (m):</label
          >
          <input
            v-model.number="stockLength"
            type="number"
            step="0.01"
            class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:border-blue-300"
          />
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Saw Kerf Width (mm):</label
          >
          <input
            v-model.number="kerfWidth"
            type="number"
            step="0.1"
            class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:border-blue-300"
          />
        </div>

        <div class="mb-4">
          <h3 class="text-lg font-medium mb-2">Required Pieces:</h3>

          <div class="space-y-3">
            <div
              v-for="(piece, index) in requiredPieces"
              :key="index"
              class="flex space-x-2"
            >
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Length (cm):</label
                >
                <input
                  v-model.number="piece.length"
                  type="number"
                  step="0.1"
                  class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:border-blue-300"
                />
              </div>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Quantity:</label
                >
                <input
                  v-model.number="piece.quantity"
                  type="number"
                  min="1"
                  class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:border-blue-300"
                />
              </div>
              <div class="flex items-end pb-1">
                <button
                  @click="
                    removePiece(index);
                    trackButtonClick('Remove Piece', 'interaction');
                  "
                  class="p-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                  :disabled="requiredPieces.length <= 1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <button
            @click="
              addPiece();
              trackButtonClick('Add Another Piece', 'interaction');
            "
            class="mt-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1 px-3 rounded-md transition duration-200"
          >
            Add Another Piece
          </button>
        </div>

        <button
          @click="
            calculateCuts();
            trackOptimization('calculate_cuts_1d');
          "
          class="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-md transition duration-200"
        >
          Calculate Optimal Cuts
        </button>
      </div>

      <div class="bg-gray-100 p-6 rounded-lg shadow">
        <h2 class="text-2xl font-semibold mb-4">Results</h2>

        <div v-if="calculationComplete" class="space-y-4">
          <div class="bg-white p-4 rounded-md">
            <p>
              <strong>Total {{ stockLength }}m Stock Bars Needed:</strong>
              {{ barsNeeded }}
            </p>
            <p>
              <strong>Total Pieces Cut:</strong>
              <span
                v-for="(piece, index) in summaryPieces"
                :key="index"
                class="mr-2"
              >
                {{ piece.quantity }} x {{ piece.length }}cm{{
                  index < summaryPieces.length - 1 ? ',' : ''
                }}
              </span>
            </p>
            <p>
              <strong>Total Leftover Material:</strong>
              {{ wasteLength.toFixed(2) }} cm ({{
                (wasteLength / 100).toFixed(2)
              }}
              m)
            </p>
            <p>
              <strong>Material Efficiency:</strong> {{ efficiency.toFixed(2) }}%
            </p>
          </div>

          <div class="mt-6">
            <h3 class="text-lg font-semibold mb-2">Cutting Diagram:</h3>
            <div ref="cuttingDiagramContainer" class="mt-4 overflow-x-auto">
              <div
                v-for="(bar, barIndex) in cuttingPattern"
                :key="barIndex"
                class="mb-10"
              >
                <p class="font-medium mb-2">Bar {{ barIndex + 1 }}:</p>
                <!-- Visual bar representation -->
                <div
                  class="flex h-16 w-full border border-gray-300 relative mb-2"
                >
                  <div
                    v-for="(piece, pieceIndex) in bar.pieces"
                    :key="pieceIndex"
                    :style="{
                      width: `${(piece.length / (stockLength * 100)) * 100}%`,
                      backgroundColor: piece.isWaste
                        ? '#f87171'
                        : getColorForPieceType(piece.pieceTypeIndex),
                    }"
                    class="h-full flex items-center justify-center text-xs text-white font-semibold relative"
                  >
                    <span class="px-1">{{ pieceIndex + 1 }}</span>
                    <div
                      v-if="pieceIndex < bar.pieces.length - 1"
                      class="absolute right-0 top-0 h-full w-0.5 bg-gray-800"
                    ></div>
                  </div>
                </div>

                <!-- Legend underneath the bar -->
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 pl-2"
                >
                  <div
                    v-for="(piece, pieceIndex) in bar.pieces"
                    :key="pieceIndex"
                    class="flex items-center"
                  >
                    <div
                      class="w-4 h-4 mr-2"
                      :style="{
                        backgroundColor: piece.isWaste
                          ? '#f87171'
                          : getColorForPieceType(piece.pieceTypeIndex),
                      }"
                    ></div>
                    <span class="text-sm">
                      <strong>{{ pieceIndex + 1 }}:</strong>
                      {{ piece.length.toFixed(1) }} cm
                      <span v-if="piece.isWaste">
                        ({{
                          ((piece.length / (stockLength * 100)) * 100).toFixed(
                            1
                          )
                        }}% - Waste)
                      </span>
                      <span v-else>
                        (Type {{ piece.pieceTypeIndex + 1 }})
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <ExportDiagramPDF
              :diagram-ready="calculationComplete"
              :calculation-data="{
                stockLength,
                requiredPieces,
                barsNeeded,
                wasteLength,
                efficiency,
                cuttingPattern,
                kerfWidth,
                summaryPieces,
              }"
            />
          </div>
        </div>

        <div v-else class="text-gray-500">
          Enter your parameters and click "Calculate" to see the cutting
          diagram.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import ExportDiagramPDF from './ExportDiagramPDF.vue';

  // Initialize analytics tracking
  const { trackButtonClick, trackOptimization } = useAnalytics();

  // Default values
  const stockLength = ref(6); // 6 meters
  const kerfWidth = ref(3); // 3 mm kerf width as default
  const calculationComplete = ref(false);
  const cuttingDiagramContainer = ref(null);

  // Required pieces with multiple sizes
  const requiredPieces = ref([
    { length: 33, quantity: 10 },
    { length: 25, quantity: 12 },
  ]);

  // Add a new piece to the list
  function addPiece() {
    requiredPieces.value.push({ length: 0, quantity: 1 });
  }

  // Remove a piece from the list
  function removePiece(index) {
    if (requiredPieces.value.length > 1) {
      requiredPieces.value.splice(index, 1);
    }
  }

  // Get piece summary for display
  const summaryPieces = ref([]);

  // Results
  const cuttingPattern = ref([]);
  const barsNeeded = ref(0);
  const wasteLength = ref(0);
  const efficiency = ref(0);

  // Generate different colors for different piece types
  const pieceColors = [
    '#60a5fa', // Blue
    '#34d399', // Green
    '#a78bfa', // Purple
    '#f97316', // Orange
    '#ec4899', // Pink
    '#14b8a6', // Teal
    '#8b5cf6', // Indigo
    '#f59e0b', // Amber
    '#06b6d4', // Cyan
  ];

  function getColorForPieceType(index) {
    return pieceColors[index % pieceColors.length];
  }

  // Calculate optimal cutting pattern for multiple piece sizes
  function calculateCuts() {
    if (
      !stockLength.value ||
      !kerfWidth.value ||
      requiredPieces.value.length === 0 ||
      requiredPieces.value.some((piece) => !piece.length || !piece.quantity)
    ) {
      alert('Please fill in all fields');
      return;
    }

    // Convert all measurements to cm for consistent calculations
    const stockLengthCm = stockLength.value * 100; // Convert stock length from meters to cm
    const kerfWidthCm = kerfWidth.value / 10; // Convert kerf width from mm to cm

    // Sort pieces by length in descending order (larger pieces first)
    // This helps with optimizing the cutting pattern
    const sortedPieces = [...requiredPieces.value].sort(
      (a, b) => b.length - a.length
    );

    // Create a flat list of all individual pieces needed
    const allPieces = [];
    sortedPieces.forEach((pieceType, typeIndex) => {
      for (let i = 0; i < pieceType.quantity; i++) {
        allPieces.push({
          length: pieceType.length,
          pieceTypeIndex: typeIndex,
        });
      }
    });

    // Store the cutting pattern
    const pattern = [];
    let remainingPieces = [...allPieces];

    // Keep adding bars until all pieces are used
    while (remainingPieces.length > 0) {
      const bar = { pieces: [] };
      let remainingBarLength = stockLengthCm;

      // Greedy algorithm: try to fit as many pieces as possible into the current bar
      let foundAPiece;
      do {
        foundAPiece = false;

        // Try to find a piece that fits in the remaining space
        for (let i = 0; i < remainingPieces.length; i++) {
          // Check if this piece fits, accounting for the kerf
          const needsKerf = bar.pieces.length > 0;
          const spaceNeeded =
            remainingPieces[i].length + (needsKerf ? kerfWidthCm : 0);

          if (spaceNeeded <= remainingBarLength) {
            // Add this piece to the bar
            const piece = remainingPieces[i];
            bar.pieces.push({
              length: piece.length,
              isWaste: false,
              pieceTypeIndex: piece.pieceTypeIndex,
            });

            // Update remaining bar length
            remainingBarLength -= spaceNeeded;

            // Remove this piece from the remaining pieces
            remainingPieces.splice(i, 1);

            foundAPiece = true;
            break;
          }
        }
      } while (foundAPiece);

      // Add waste piece if there's any left
      if (remainingBarLength > 0) {
        bar.pieces.push({
          length: remainingBarLength,
          isWaste: true,
        });
      }

      pattern.push(bar);
    }

    // Calculate waste and efficiency
    let totalWaste = 0;

    for (const bar of pattern) {
      for (const piece of bar.pieces) {
        if (piece.isWaste) {
          totalWaste += piece.length;
        }
      }
    }

    const totalMaterialUsed = pattern.length * stockLengthCm;

    // Calculate total useful material (sum of all piece lengths)
    let totalUsefulMaterial = 0;
    for (const pieceType of requiredPieces.value) {
      totalUsefulMaterial += pieceType.length * pieceType.quantity;
    }

    const efficiencyPercent = (totalUsefulMaterial / totalMaterialUsed) * 100;

    // Update the refs with calculated values
    cuttingPattern.value = pattern;
    barsNeeded.value = pattern.length;
    wasteLength.value = totalWaste;
    efficiency.value = efficiencyPercent;
    summaryPieces.value = [...requiredPieces.value];
    calculationComplete.value = true;
  }

  // Call the calculation function on mount with default values
  onMounted(() => {
    calculateCuts();
  });
</script>

<style scoped>
  /* Additional styling if needed */
</style>
